interface XyzBounds {
  xMin: number
  xMax: number
  yMin: number
  yMax: number
  zMin: number
  zMax: number
}

const expandByPoint = (
  bounds: XyzBounds,
  point: { x: number; y: number; z: number },
): XyzBounds => ({
  xMin: Math.min(bounds.xMin, point.x),
  xMax: Math.max(bounds.xMax, point.x),
  yMin: Math.min(bounds.yMin, point.y),
  yMax: Math.max(bounds.yMax, point.y),
  zMin: Math.min(bounds.zMin, point.z),
  zMax: Math.max(bounds.zMax, point.z),
})

const expandByPoints = (bounds: XyzBounds, points: { x: number; y: number; z: number }[]) =>
  points.reduce(expandByPoint, bounds)

const emptyBounds = (): XyzBounds => ({
  xMin: Infinity,
  xMax: -Infinity,
  yMin: Infinity,
  yMax: -Infinity,
  zMin: Infinity,
  zMax: -Infinity,
})

const isValidBounds = (bounds: XyzBounds) =>
  Number.isFinite(bounds.xMin) &&
  Number.isFinite(bounds.xMax) &&
  Number.isFinite(bounds.yMin) &&
  Number.isFinite(bounds.yMax)

/**
 * XY bounds of all model elements except foundation, matching backend `PlanarModel.xyz_bounds`.
 */
const getModelXyzBounds = (model: PlanarModel): XyzBounds => {
  let bounds = emptyBounds()

  const polygonElements = [
    ...model.walls,
    ...model.slabs,
    ...model.roof_slabs,
    ...model.vertical_slabs,
    ...model.vertical_roof_slabs,
  ]
  polygonElements.forEach(element => {
    bounds = expandByPoints(bounds, element.shape.points)
  })

  const lineElements = [...model.beams, ...model.purlins, ...model.columns]
  lineElements.forEach(element => {
    bounds = expandByPoint(bounds, element.shape.start)
    bounds = expandByPoint(bounds, element.shape.end)
  })

  const positionElements = [...model.rips, ...model.lintels]
  positionElements.forEach(element => {
    bounds = expandByPoint(bounds, element.start)
    bounds = expandByPoint(bounds, element.end)
  })

  if (!isValidBounds(bounds)) {
    return { xMin: 0, xMax: 0, yMin: 0, yMax: 0, zMin: 0, zMax: 0 }
  }

  return bounds
}

export type { XyzBounds }
export default getModelXyzBounds
